import type { AttendanceFilterValue } from "views/Attendance/AttendanceFilterPanel"
import type { AttendanceDetailSelection } from "views/Attendance/AttendanceTable"
import type { RequestLeaveItem } from "views/RequestLeave/types"

import { RequestLeaveStatus } from "__generated__/graphql"
import { useQuery } from "@apollo/client"
import { Alert, Box, Button, Collapse, Typography } from "@mui/material"
import { FilterCollapseButton, Flex, FlexColumn, ViewModeToggle } from "components/common"
import { DateTime } from "luxon"
import { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { dataViewPreferencesSelector, updateAttendancePreferences } from "reducers/dataViewPreferencesSlice"
import { profileSelector } from "reducers/profileSlice"
import AttendanceDetailDrawer from "views/Attendance/AttendanceDetailDrawer"
import AttendanceFilterPanel from "views/Attendance/AttendanceFilterPanel"
import AttendanceOverview from "views/Attendance/AttendanceOverview"
import AttendanceTable from "views/Attendance/AttendanceTable"
import AttendanceCalendar from "views/Attendance/Calendar/AttendanceCalendar"
import { FETCH_ATTENDANCES } from "views/Attendance/graphql"
import { filterAttendances, getDateRange, getGroupedByUsers } from "views/Attendance/utils"
import { FIND_REQUEST_LEAVES } from "views/RequestLeave/graphql"

const ATTENDANCE_VIEW_OPTIONS = [
  { label: "Chấm công cá nhân", value: "personal" },
  { label: "Bảng công chung", value: "common" },
] as const

type AttendanceVisibilityFilter = Pick<
  AttendanceFilterValue,
  "showChat" | "showCheckins" | "showFullTime" | "showOvertime" | "showPartTime"
>

const AttendanceBoard = () => {
  const dispatch = useDispatch()
  const { user } = useSelector(profileSelector)
  const { attendance: preferences } = useSelector(dataViewPreferencesSelector)
  const currentAttendeeId = user?.attendee?.id
  const startDate = useMemo(() => DateTime.fromISO(preferences.startDate), [preferences.startDate])
  const endDate = useMemo(() => DateTime.fromISO(preferences.endDate), [preferences.endDate])

  const filtersExpanded = preferences.filtersExpanded ?? false
  const [visibilityFilter, setVisibilityFilter] = useState<AttendanceVisibilityFilter>({
    showChat: false,
    showCheckins: true,
    showFullTime: true,
    showOvertime: false,
    showPartTime: true,
  })
  const [selectedDetail, setSelectedDetail] = useState<AttendanceDetailSelection | null>(null)

  const filter = useMemo<AttendanceFilterValue>(
    () => ({
      ...visibilityFilter,
      attendees: preferences.attendees,
      endDate,
      showPersonalView: preferences.mode === "personal",
      startDate,
    }),
    [endDate, preferences.attendees, preferences.mode, startDate, visibilityFilter],
  )

  const selectedAttendeeIds = useMemo(() => filter.attendees.map((attendee) => attendee.id), [filter.attendees])
  const attendanceQueryAttendeeIds = filter.showPersonalView
    ? currentAttendeeId
      ? [currentAttendeeId]
      : undefined
    : selectedAttendeeIds.length > 0
    ? Array.from(new Set([...selectedAttendeeIds, ...(currentAttendeeId ? [currentAttendeeId] : [])]))
    : undefined

  const {
    data,
    error: attendanceError,
    loading: attendanceLoading,
    refetch: refetchAttendances,
  } = useQuery(FETCH_ATTENDANCES, {
    skip: filter.showPersonalView && !currentAttendeeId,
    variables: {
      attendeeIds: attendanceQueryAttendeeIds,
      endDate: filter.endDate.toFormat("yyyy-MM-dd"),
      startDate: filter.startDate.toFormat("yyyy-MM-dd"),
    },
  })

  const {
    data: leaveData,
    error: leaveError,
    loading: leaveLoading,
    refetch: refetchLeaves,
  } = useQuery(FIND_REQUEST_LEAVES, {
    skip: filter.showPersonalView && !currentAttendeeId,
    variables: {
      attendeeId: filter.showPersonalView ? currentAttendeeId : undefined,
      endDate: filter.endDate.toFormat("yyyy-MM-dd"),
      startDate: filter.startDate.toFormat("yyyy-MM-dd"),
    },
  })

  const queriedAttendances = useMemo(() => data?.attendances ?? [], [data])
  const commonViewAttendances = useMemo(() => {
    if (filter.showPersonalView || selectedAttendeeIds.length === 0) return queriedAttendances

    const selectedIds = new Set(selectedAttendeeIds)
    return queriedAttendances.filter((attendance) => selectedIds.has(attendance.attendee.id))
  }, [filter.showPersonalView, queriedAttendances, selectedAttendeeIds])

  const filteredAttendances = useMemo(() => {
    return filterAttendances(commonViewAttendances, {
      showChat: filter.showChat,
      showCheckins: filter.showCheckins,
      showFullTime: filter.showPersonalView ? true : filter.showFullTime,
      showOvertime: filter.showOvertime,
      showPartTime: filter.showPersonalView ? true : filter.showPartTime,
    })
  }, [commonViewAttendances, filter])

  const groupedByUsers = useMemo(() => {
    const groups = getGroupedByUsers(filteredAttendances)
    if (!currentAttendeeId) return groups

    return [...groups].sort((a, b) => {
      if (a.attendee.id === currentAttendeeId) return -1
      if (b.attendee.id === currentAttendeeId) return 1
      return 0
    })
  }, [currentAttendeeId, filteredAttendances])

  const currentUserAttendances = useMemo(() => {
    if (!currentAttendeeId) return []

    return filterAttendances(
      queriedAttendances.filter((attendance) => attendance.attendee.id === currentAttendeeId),
      {
        showChat: filter.showChat,
        showCheckins: filter.showCheckins,
        showFullTime: true,
        showOvertime: filter.showOvertime,
        showPartTime: true,
      },
    )
  }, [currentAttendeeId, filter.showChat, filter.showCheckins, filter.showOvertime, queriedAttendances])

  const requestLeaves = useMemo(() => leaveData?.findRequestLeaves ?? [], [leaveData])
  const currentUserLeaves = useMemo(() => {
    if (!currentAttendeeId) return []
    return requestLeaves.filter((leave) => leave.attendee.id === currentAttendeeId)
  }, [currentAttendeeId, requestLeaves])

  const leavesByAttendeeDate = useMemo(() => {
    const map = new Map<string, RequestLeaveItem[]>()

    requestLeaves.forEach((leave) => {
      if (leave.status === RequestLeaveStatus.Rejected) return

      getDateRange(DateTime.fromISO(leave.startDate), DateTime.fromISO(leave.endDate)).forEach((date) => {
        const key = `${leave.attendee.id}-${date.toFormat("yyyy-MM-dd")}`
        const records = map.get(key) ?? []
        records.push(leave)
        map.set(key, records)
      })
    })

    return map
  }, [requestLeaves])

  const dateRange = useMemo(() => getDateRange(filter.startDate, filter.endDate), [filter.endDate, filter.startDate])
  const hasError = Boolean(attendanceError || leaveError)
  const loading = attendanceLoading || leaveLoading

  const handleFilterChange = (patch: Partial<AttendanceFilterValue>) => {
    if (patch.attendees || patch.endDate || patch.startDate) {
      dispatch(
        updateAttendancePreferences({
          ...(patch.attendees ? { attendees: patch.attendees } : {}),
          ...(patch.endDate ? { endDate: patch.endDate.toFormat("yyyy-MM-dd") } : {}),
          ...(patch.startDate ? { startDate: patch.startDate.toFormat("yyyy-MM-dd") } : {}),
        }),
      )
    }

    setVisibilityFilter((current) => ({
      showChat: patch.showChat ?? current.showChat,
      showCheckins: patch.showCheckins ?? current.showCheckins,
      showFullTime: patch.showFullTime ?? current.showFullTime,
      showOvertime: patch.showOvertime ?? current.showOvertime,
      showPartTime: patch.showPartTime ?? current.showPartTime,
    }))
  }

  const shiftPeriod = (delta: number) => {
    dispatch(
      updateAttendancePreferences({
        endDate: filter.endDate.plus({ months: delta }).toFormat("yyyy-MM-dd"),
        startDate: filter.startDate.plus({ months: delta }).toFormat("yyyy-MM-dd"),
      }),
    )
  }

  const handleRetry = () => {
    void Promise.all([refetchAttendances(), refetchLeaves()])
  }

  return (
    <FlexColumn flex={1} gap={2} minHeight={0}>
      <FlexColumn>
        <Flex alignItems="flex-start" className="flex-wrap" gap={2} justifyContent="space-between">
          <Box>
            <Typography variant="h5">Quản lý chấm công</Typography>
            <Typography color="text.secondary" variant="body2">
              Theo dõi thời gian làm việc, đi muộn và các yêu cầu liên quan.
            </Typography>
          </Box>
          <Flex alignItems="center" className="flex-wrap" gap={1} justifyContent="flex-end">
            <ViewModeToggle
              ariaLabel="Chế độ xem chấm công"
              onChange={(mode) => {
                dispatch(updateAttendancePreferences({ mode }))
              }}
              options={ATTENDANCE_VIEW_OPTIONS}
              value={filter.showPersonalView ? "personal" : "common"}
            />
            <FilterCollapseButton
              controlsId="attendance-filters"
              expanded={filtersExpanded}
              onClick={() => dispatch(updateAttendancePreferences({ filtersExpanded: !filtersExpanded }))}
            />
          </Flex>
        </Flex>

        <Collapse id="attendance-filters" in={filtersExpanded} sx={{ "&.MuiCollapse-hidden": { display: "none" } }}>
          <Box pt={2}>
            <AttendanceFilterPanel
              onChange={handleFilterChange}
              onShiftPeriod={shiftPeriod}
              resultLabel={
                filter.showPersonalView ? `${currentUserAttendances.length} lượt` : `${groupedByUsers.length} nhân viên`
              }
              value={filter}
            />
          </Box>
        </Collapse>
      </FlexColumn>

      {hasError && (
        <Alert
          action={
            <Button color="inherit" onClick={handleRetry} size="small">
              Thử lại
            </Button>
          }
          severity="error"
        >
          Không thể tải đầy đủ dữ liệu chấm công.
        </Alert>
      )}

      {currentAttendeeId && (
        <AttendanceOverview
          attendances={currentUserAttendances}
          attendeeName={user?.attendee?.name}
          endDate={filter.endDate}
          leaves={currentUserLeaves}
          startDate={filter.startDate}
        />
      )}

      {filter.showPersonalView ? (
        currentAttendeeId ? (
          <AttendanceCalendar
            attendances={currentUserAttendances}
            attendeeName={user?.attendee?.name}
            endDate={filter.endDate}
            leaves={currentUserLeaves}
            startDate={filter.startDate}
          />
        ) : (
          <Alert severity="warning">Tài khoản chưa được liên kết với nhân viên chấm công.</Alert>
        )
      ) : (
        <AttendanceTable
          dateRange={dateRange}
          groups={groupedByUsers}
          hasError={hasError}
          leavesByAttendeeDate={leavesByAttendeeDate}
          loading={loading}
          onSelectDetail={setSelectedDetail}
          showCheckins={filter.showCheckins}
          showOvertime={filter.showOvertime}
        />
      )}

      <AttendanceDetailDrawer
        attendances={selectedDetail?.attendances ?? []}
        attendeeName={selectedDetail?.attendeeName}
        date={selectedDetail?.date ?? null}
        leaves={selectedDetail?.leaves ?? []}
        onClose={() => setSelectedDetail(null)}
      />
    </FlexColumn>
  )
}

export default AttendanceBoard
